import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { decodeEpointData, verifySignature } from "../services/epoint";
import { getEpointConfig } from "../services/firestore";
import { markOrderPlaced, markOrderCancelled } from "../services/orderPayment";

/**
 * /result — server-to-server callback endpoint used by Epoint to notify the
 * merchant of the final transaction status (independent of whether the user
 * lands on /success).
 *
 * Caveat: since this app has no real backend, the route is just an SPA page.
 * Epoint POSTs `data` + `signature`; we can only react when the browser also
 * happens to load this URL (it generally won't from a server-to-server call).
 *
 * The page is still useful for QA — paste a result link in the browser to
 * verify Epoint's signature and toggle the order status manually.
 */
export default function PaymentResultPage() {
  const [params] = useSearchParams();
  const [msg, setMsg] = useState("İşlənir…");

  useEffect(() => {
    let cancelled = false;
    async function run() {
      const data = params.get("data") || "";
      const signature = params.get("signature") || "";
      if (!data || !signature) {
        if (!cancelled) setMsg("data və signature parametrləri tapılmadı.");
        return;
      }
      const cfg = await getEpointConfig();
      if (!cfg?.private_key) {
        if (!cancelled) setMsg("Epoint konfiqurasiyası tamamlanmayıb.");
        return;
      }
      const ok = await verifySignature(cfg.private_key.trim(), data, signature);
      if (!ok) {
        if (!cancelled) setMsg("İmza yoxlaması uğursuz oldu — sifariş status dəyişdirilmədi.");
        return;
      }
      const payload = decodeEpointData(data);
      const orderId = payload?.order_id;
      const status = (payload?.status || "").toLowerCase();
      if (!orderId) {
        if (!cancelled) setMsg("order_id tapılmadı.");
        return;
      }
      if (status === "success" || status === "ok") {
        await markOrderPlaced(orderId, { transaction: payload.transaction || "" });
        if (!cancelled) setMsg(`Sifariş ${orderId} → ödənilib`);
      } else {
        await markOrderCancelled(orderId, {
          failure_reason: payload.message || payload.description || status,
        });
        if (!cancelled) setMsg(`Sifariş ${orderId} → ləğv olundu`);
      }
    }
    run();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="bg-[var(--bg)] min-h-[70vh]">
      <section className="container-base py-20">
        <div className="max-w-2xl mx-auto text-center">
          <div className="w-12 h-12 mx-auto mb-4 text-[var(--primary)]">
            <Loader2 className="animate-spin" size={48} />
          </div>
          <h1 className="font-display text-3xl mb-2">Epoint nəticə işləyicisi</h1>
          <p className="text-[var(--text-2)]">{msg}</p>
        </div>
      </section>
    </div>
  );
}
